<template>
  <div class="profile-page-shell">
    <Edit
      v-if="getCustomer"
      :editDialog="editDialog"
      :editCustomer="getCustomer"
      @close="editDialog = false"
      @submit="loadData"
    />
    <ParentInvoiceCreate
      v-if="getCustomer"
      :createDialog="createParentInvoiceDialog"
      :parent="getCustomer"
      @close="createParentInvoiceDialog = false"
      @submit="invoiceCreated"
    />

    <div class="profile-topbar">
      <v-btn text color="primary" class="profile-back-button" to="/parents/list">
        <v-icon left>mdi-arrow-left</v-icon><span>Parents</span>
      </v-btn>
      <div class="profile-record-navigation" aria-label="Navigate parent records">
        <v-btn
          icon
          small
          :disabled="!canMoveBack"
          aria-label="Previous parent"
          @click="moveBack"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <span
          >Parent #{{
            getCustomer && getCustomer.localId ? getCustomer.localId : "—"
          }}</span
        >
        <v-btn
          icon
          small
          :disabled="!canMoveNext"
          aria-label="Next parent"
          @click="moveNext"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-skeleton-loader
      v-if="loading"
      type="card, list-item-three-line"
      class="profile-skeleton"
    />

    <template v-else-if="getCustomer && getCustomer.id">
      <v-alert
        v-if="getCustomer.alert"
        class="profile-alert"
        color="primary"
        text
        border="left"
        icon="mdi-alert-circle-outline"
      >
        <div class="profile-alert__title">Important note</div>
        <div>{{ getCustomer.note }}</div>
      </v-alert>

      <v-card class="profile-hero-card" elevation="0">
        <div class="profile-hero">
          <div class="profile-hero__identity">
            <div class="profile-hero__avatar">{{ initials(getCustomer.name) }}</div>
            <div>
              <div class="profile-hero__eyebrow">Parent profile</div>
              <h1 class="profile-hero__title">{{ getCustomer.name }}</h1>
              <div class="profile-hero__meta">
                <span
                  ><v-icon size="15">mdi-clock-outline</v-icon> Added
                  {{ getCustomer.createdAt | moment("from", "now") }}</span
                >
                <span v-if="getCustomer.address"
                  ><v-icon size="15">mdi-map-marker-outline</v-icon>
                  {{ getCustomer.city }}, {{ getCustomer.state }}</span
                >
              </div>
            </div>
          </div>
          <div class="profile-hero__actions">
            <v-btn
              v-if="checkRightStatus(6)"
              color="primary"
              depressed
              @click="createParentInvoiceDialog = true"
            >
              <v-icon left size="19">mdi-receipt-text-plus-outline</v-icon>Quick
              fee invoice
            </v-btn>
            <v-btn outlined color="primary" @click="editDialog = true"
              ><v-icon left size="18">mdi-pencil-outline</v-icon>Edit</v-btn
            >
            <v-menu bottom left offset-y>
              <template v-slot:activator="{ on, attrs }"
                ><v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  outlined
                  color="primary"
                  aria-label="More parent actions"
                  ><v-icon>mdi-dots-horizontal</v-icon></v-btn
                ></template
              >
              <v-list dense class="action-menu">
                <v-list-item @click="viewOpenInvoice"
                  ><v-list-item-icon
                    ><v-icon size="19"
                      >mdi-file-document-outline</v-icon
                    ></v-list-item-icon
                  ><v-list-item-title
                    >Open invoices</v-list-item-title
                  ></v-list-item
                >
                <v-list-item @click="viewBalanceLetter"
                  ><v-list-item-icon
                    ><v-icon size="19"
                      >mdi-email-outline</v-icon
                    ></v-list-item-icon
                  ><v-list-item-title
                    >Balance letter</v-list-item-title
                  ></v-list-item
                >
                <SyncBalance />
                <Statement :CustomerId="getCustomer.id" />
                <PaymentCollectLink
                  :id="getCustomer.id"
                  :customer="getCustomer"
                />
              </v-list>
            </v-menu>
          </div>
        </div>

        <v-divider />
        <v-card-text class="profile-content-grid">
          <div class="profile-info-grid">
            <section class="profile-info-card">
              <div class="profile-info-label">
                <v-icon color="primary" size="18">mdi-account-outline</v-icon
                >Dad
              </div>
              <div class="profile-info-name">
                {{ getCustomer.fatherName || "Not provided" }}
              </div>
              <a
                v-if="getCustomer.fatherPhoneNumber"
                :href="`tel:${getCustomer.fatherPhoneNumber}`"
                class="profile-info-link"
                ><v-icon size="16">mdi-phone-outline</v-icon
                >{{ getCustomer.fatherPhoneNumber | USPhoneNumber }}</a
              >
              <span v-else class="profile-info-empty">No phone number</span>
              <a
                v-if="getCustomer.fatherEmailAddress"
                :href="`mailto:${getCustomer.fatherEmailAddress}`"
                class="profile-info-link"
                ><v-icon size="16">mdi-email-outline</v-icon
                >{{ getCustomer.fatherEmailAddress }}</a
              >
              <span v-else class="profile-info-empty">No email address</span>
            </section>

            <section class="profile-info-card">
              <div class="profile-info-label">
                <v-icon color="primary" size="18">mdi-account-outline</v-icon
                >Mom
              </div>
              <div class="profile-info-name">
                {{ getCustomer.motherName || "Not provided" }}
              </div>
              <a
                v-if="getCustomer.motherPhoneNumber"
                :href="`tel:${getCustomer.motherPhoneNumber}`"
                class="profile-info-link"
                ><v-icon size="16">mdi-phone-outline</v-icon
                >{{ getCustomer.motherPhoneNumber | USPhoneNumber }}</a
              >
              <span v-else class="profile-info-empty">No phone number</span>
              <a
                v-if="getCustomer.motherEmailAddress"
                :href="`mailto:${getCustomer.motherEmailAddress}`"
                class="profile-info-link"
                ><v-icon size="16">mdi-email-outline</v-icon
                >{{ getCustomer.motherEmailAddress }}</a
              >
              <span v-else class="profile-info-empty">No email address</span>
            </section>

            <section class="profile-info-card profile-info-card--full">
              <div class="profile-info-label">
                <v-icon color="primary" size="18">mdi-map-marker-outline</v-icon
                >Address
              </div>
              <button
                v-if="getCustomer.address"
                type="button"
                class="profile-address-button"
                @click="openGoogleMap"
              >
                <strong>{{ getCustomer.address }}</strong>
                <span
                  >{{ getCustomer.city }}, {{ getCustomer.state }}
                  {{ getCustomer.zip }}</span
                >
                <small
                  >Open in Google Maps
                  <v-icon size="13">mdi-open-in-new</v-icon></small
                >
              </button>
              <span v-else class="profile-info-empty">No address provided</span>
            </section>
          </div>

          <aside class="profile-summary-panel">
            <div class="profile-summary-label">Current balance</div>
            <div class="profile-summary-value" :class="{ overdue: isOverLimit }">
              {{ getCustomer.balance | currency }}
            </div>
            <div class="profile-summary-breakdown">
              <div>
                <span>Parent responsibility</span
                ><strong>{{ studentFee.totalCustomerFee | currency }}</strong>
              </div>
              <div v-if="studentFee.totalCharity > 0">
                <span>Masjid responsibility</span
                ><strong>{{ studentFee.totalCharity | currency }}</strong>
              </div>
              <div v-if="studentFee.totalDiscount > 0">
                <span>Discounts</span
                ><strong>{{ studentFee.totalDiscount | currency }}</strong>
              </div>
            </div>
            <div v-if="paymentMethods.length" class="profile-summary-tags">
              <span class="profile-summary-label">Payment methods</span>
              <v-chip
                v-for="method in paymentMethods"
                :key="method"
                x-small
                color="primary"
                outlined
                >{{ method }}</v-chip
              >
            </div>
          </aside>
        </v-card-text>
      </v-card>

      <div class="profile-tabs-wrap">
        <v-tabs
          v-model="active"
          color="primary"
          slider-color="primary"
          show-arrows
          class="profile-tabs"
        >
          <v-tab v-for="tab in tabs" :key="tab.label"
            ><v-icon left size="18">{{ tab.icon }}</v-icon
            >{{ tab.label }}</v-tab
          >
        </v-tabs>
      </div>

      <v-tabs-items v-model="active" touchless class="profile-tab-content">
        <v-tab-item><Dashboard /></v-tab-item>
        <v-tab-item
          ><StudentList showBalance="hide" :CustomerId="getCustomer.id"
        /></v-tab-item>
        <v-tab-item
          ><InvoiceList
            toggleStatus="hide"
            :internalUse="true"
            :showAll="true"
            :CustomerId="getCustomer.id"
        /></v-tab-item>
        <v-tab-item><PaymentList :CustomerId="getCustomer.id" /></v-tab-item>
        <v-tab-item
          ><AdvancePaymentList :CustomerId="getCustomer.id"
        /></v-tab-item>
        <v-tab-item
          ><Subscription toggleStatus="hide" :CustomerId="getCustomer.id"
        /></v-tab-item>
        <v-tab-item
          ><AttachmentList TypeId="1" :RelationId="getCustomer.id"
        /></v-tab-item>
      </v-tabs-items>
    </template>

    <v-alert v-else type="warning" text
      >We could not load this parent profile.</v-alert
    >
  </div>
</template>

<script>
import customerService from "@/modules/Customer/service.js";
import { mapGetters, mapActions } from "vuex";
import Hashids from "hashids";
const hashids = new Hashids();
const Dashboard = () => import("@/modules/Customer/components/Dashboard.vue");
const InvoiceList = () => import("@/modules/Invoice/components/List");
const StudentList = () => import("@/modules/Student/components/List");
const AttachmentList = () => import("@/modules/Attachment/components/List");
const AdvancePaymentList = () =>
  import("@/modules/AdvancePayment/components/List");
const PaymentList = () =>
  import("@/modules/Invoice/Payment/components/List.vue");
const PaymentCollectLink = () =>
  import("@/modules/Customer/components/PaymentCollectLink.vue");
const SyncBalance = () =>
  import("@/modules/Customer/components/syncBalance.vue");
const Statement = () => import("@/modules/Customer/components/Statement");
const ParentInvoiceCreate = () =>
  import("@/modules/Customer/components/ParentInvoiceCreate.vue");
const Subscription = () => import("@/modules/Subscription/components/List.vue");
const Edit = () => import("@/modules/Customer/components/Edit");

export default {
  name: "customer-profile",
  components: {
    Edit,
    StudentList,
    PaymentCollectLink,
    Subscription,
    SyncBalance,
    Dashboard,
    Statement,
    InvoiceList,
    AttachmentList,
    AdvancePaymentList,
    PaymentList,
    ParentInvoiceCreate,
  },
  data() {
    return {
      active: 0,
      customerCurrentIndex: -1,
      createParentInvoiceDialog: false,
      editDialog: false,
      id: null,
      loading: true,
      studentFee: { totalCustomerFee: 0, totalCharity: 0, totalDiscount: 0 },
      tabs: [
        { label: "Overview", icon: "mdi-view-dashboard-outline" },
        { label: "Students", icon: "mdi-account-school-outline" },
        { label: "Invoices", icon: "mdi-receipt-text-outline" },
        { label: "Payments", icon: "mdi-cash-check" },
        { label: "Advance payments", icon: "mdi-cash-fast" },
        { label: "Subscriptions", icon: "mdi-calendar-sync-outline" },
        { label: "Files", icon: "mdi-paperclip" },
      ],
    };
  },
  computed: {
    ...mapGetters("customer", ["getCustomer", "customerList"]),
    ...mapGetters("global", ["checkRightStatus"]),
    canMoveBack() {
      return this.customerCurrentIndex > 0;
    },
    canMoveNext() {
      return (
        this.customerCurrentIndex >= 0 &&
        this.customerCurrentIndex < this.customerList.length - 1
      );
    },
    isOverLimit() {
      return (
        Number(this.getCustomer.balance) > Number(this.getCustomer.balanceLimit)
      );
    },
    paymentMethods() {
      const methods = [];
      if (this.getCustomer.payingCash) methods.push("Cash");
      if (this.getCustomer.stripeId) methods.push("Stripe");
      if (this.getCustomer.mohidId) methods.push("Mohid");
      if (
        this.getCustomer.MerchantCustomers &&
        this.getCustomer.MerchantCustomers.length
      )
        methods.push("Online");
      return methods;
    },
  },
  watch: {
    "$route.params.id"(next, previous) {
      if (next && next !== previous) this.loadData();
    },
  },
  async created() {
    if (this.$route.params.tab !== undefined)
      this.active = Number(this.$route.params.tab);
    await this.loadData();
    this.$events.listen("customerProfileEvent", this.loadData);
  },
  beforeDestroy() {
    this.$events.remove("customerProfileEvent");
  },
  methods: {
    ...mapActions("customer", ["setCustomer"]),
    initials(name) {
      return (name || "P")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },
    setCurrentIndex() {
      this.customerCurrentIndex = this.customerList.findIndex(
        (item) => String(item.id) === String(this.id)
      );
    },
    async navigateCustomer(index) {
      if (index < 0 || index >= this.customerList.length) return;
      const customer = this.customerList[index];
      this.customerCurrentIndex = index;
      await this.$router.push({ path: `/parents/${customer.id}/profile` });
      this.refreshTabData();
    },
    moveBack() {
      this.navigateCustomer(this.customerCurrentIndex - 1);
    },
    moveNext() {
      this.navigateCustomer(this.customerCurrentIndex + 1);
    },
    refreshTabData() {
      [
        "invoiceListEvent",
        "paymentListEvent",
        "studentListEvent",
        "subscriptionListEvent",
      ].forEach((event) => this.$events.emit(event));
    },
    async loadData() {
      this.loading = true;
      this.id = this.$route.params.id;
      try {
        await this.setCustomer(this.id);
        await this.getFeeAmount();
        this.setCurrentIndex();
      } finally {
        this.loading = false;
      }
    },
    getFeeAmount() {
      return customerService.getFeeByCustomerId(this.id).then((response) => {
        this.studentFee = response.data;
      });
    },
    invoiceCreated() {
      this.createParentInvoiceDialog = false;
      this.$events.emit("invoiceListEvent");
      this.loadData();
    },
    viewOpenInvoice() {
      const route = this.$router.resolve({
        path: `/global/invoice/${hashids.encode(
          this.getCustomer.id
        )}/openInvoice`,
      });
      window.open(route.href, "newwindow", "width=850, height=1100");
    },
    viewBalanceLetter() {
      const route = this.$router.resolve({
        path: `/global/invoice/${hashids.encode(
          this.getCustomer.id
        )}/PendingBalanceLetter`,
      });
      window.open(route.href, "newwindow", "width=850, height=1100");
    },
    openGoogleMap() {
      const customer = this.getCustomer;
      window.open(
        `https://www.google.com/maps/place/${customer.address} ${customer.city},${customer.state} ${customer.zip}`,
        "_blank"
      );
    },
  },
};
</script>

<style scoped>
/* Shared profile layout styles come from app.scss. */

.profile-summary-value.overdue {
  color: #2e7d32;
}

@media (max-width: 700px) {
  .profile-hero__actions .v-btn:first-child {
    flex: 1;
  }

  .profile-hero__actions .v-btn:nth-child(2) {
    min-width: 42px;
    padding: 0 11px;
  }
}

@media (max-width: 420px) {
  .profile-hero__actions .v-btn:nth-child(2) .v-icon {
    margin: 0 !important;
  }

  .profile-hero__actions .v-btn:nth-child(2) {
    font-size: 0;
  }
}
</style>
